import { useLocale, useTranslations } from 'next-intl';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { useEffect, useState, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { DatePicker } from '@/component/DateRange';
import { DefaultDropdown } from '@/component/Dropdown';
import { useListingSearchStore } from '@/store';
import { CalendarIcon } from '@/component/Icons/CalendarIcon';
import { CloseIcon } from '@/component/Icons/CloseIcon';
import { showDateRange, showFullDate, showShortDate } from '@/utils/common';
import { DateModal } from './DateModal';
import styles from './DateField.module.css';

export const SearchListingDateField = ({ currentStep, onSetStep, theme }) => {
  const {
    date,
    setDate,
    invalidField,
    setInvalidField,
    focusField,
    setFocusField,
    setInexactStay,
    inexactStay,
  } = useListingSearchStore();
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useMediaQuery({ query: '(max-width: 640px)' });
  const isMobileRef = useRef(isMobile);

  const t = useTranslations();

  const isFocused = focusField === 'date';

  const handleOpenDropdown = (e) => {
    // HACK: if we clicked to clear button, we don't want to open anything
    if (e.target.tagName.toLowerCase() === 'button') return;
    if (!isMobile) {
      setTimeout(() => {
        document
          .getElementById('search-section')
          .scrollIntoView({ block: 'end' });
      }, 100);
    }
    if (isMobile && !currentStep) {
      onSetStep('date');
      return;
    }
    if (!currentStep) {
      setIsVisible(true);
      setFocusField('date');
    }
  };

  const handleCloseDropdown = () => {
    setIsVisible(false);
    if (isFocused) {
      setFocusField(null);
      onSetStep(null);
    }
  };

  const ref = useDetectClickOutside({ onTriggered: handleCloseDropdown });

  const handleClearValue = () => {
    setDate();
  };

  const handleCloseModal = (e) => {
    if (e) e.stopPropagation();
    setFocusField(null);
    onSetStep(null);
  };

  const handleNextStep = (e) => {
    e.stopPropagation();
    onSetStep('guests');
    setFocusField(null);
    setIsVisible(false);
  };

  useEffect(() => {
    if (invalidField === 'date') {
      setIsVisible(true);
      setInvalidField(null);
    }
  }, [invalidField]);

  useEffect(() => {
    if (isMobileRef.current !== isMobile && !isMobile) {
      handleCloseModal();
    }
    isMobileRef.current = isMobile;
  }, [isMobile]);

  const handleBlur = (e) => {
    if (
      e.relatedTarget &&
      ref.current &&
      !ref.current.contains(e.relatedTarget)
    ) {
      setFocusField(null);
      setIsVisible(false);
    }
  };

  return (
    <section
      className={`date-field ${styles.date_field} ${
        isFocused ? '!bg-white' : ''
      } ${theme === 'compact' ? styles.compact : ''}
      ${theme === 'vertical' ? styles.vertical : ''}`}
      ref={ref}
      tabIndex={1}
      onFocus={handleOpenDropdown}
      onBlur={handleBlur}
    >
      {theme === 'compact' ? (
        <DateViewerCompact
          label={t('check-in')}
          value={date}
          onReset={handleClearValue}
          isFocused={isFocused}
        />
      ) : (
        <>
          <DateViewer
            label={t('check-in')}
            value={date?.startDate}
            onReset={handleClearValue}
            isFocused={isFocused}
            theme={theme}
          />
          <Divider />
          <DateViewer
            label={t('check-out')}
            value={date?.endDate}
            onReset={handleClearValue}
            isFocused={isFocused}
            theme={theme}
          />
        </>
      )}

      {isVisible && (
        <DefaultDropdown className={styles.daterange_dropdown}>
          <DatePicker
            value={date}
            onChange={setDate}
            onSelectInexactDays={setInexactStay}
            inexactStay={inexactStay}
          />
        </DefaultDropdown>
      )}

      {currentStep === 'date' && isMobile && (
        <DateModal
          onClose={handleCloseModal}
          onSubmit={handleNextStep}
          date={date}
          onDateChange={setDate}
          inexactStay={inexactStay}
          onSelectInexactDays={setInexactStay}
        />
      )}
    </section>
  );
};

function Divider() {
  return (
    <div className="w-[1px] h-[44px] flex-grow-1 flex-shrink-0 basis-auto my-auto mx-[10px] bg-slate-200" />
  );
}

function DateViewer({ label, value, isFocused, onReset, theme }) {
  const locale = useLocale();
  const t = useTranslations();
  const isMobile = useMediaQuery({ query: '(max-width: 640px)' });
  const formatDate = isMobile ? showShortDate : showFullDate;
  return (
    <div
      className={`date-box search-input-box ${styles.box} ${
        isFocused ? 'bg-slate-100 !border-slate-300' : ''
      }`}
    >
      <div className={styles.input}>
        <span className={`${styles.icon} ${isFocused ? '!text-teal-600' : ''}`}>
          <CalendarIcon />
        </span>
        <div className="flex flex-col gap-2 min-w-0 search-item-box">
          <label className="text-xs text-slate-600 truncate inline-block h-[18px] mt-[-4px] mb-[-8px]">
            <span>{label}</span>
          </label>
          {!value && (
            <span
              className={`inline-block font-medium text-slate-400 mt-[-5px] mb-[-7px] truncate search-item-input max-sm:text-sm ${
                theme === 'vertical' ? 'text-[14px]' : ''
              }`}
            >
              {t('add-dates')}
            </span>
          )}
          {value && (
            <span
              className={`inline-block font-medium text-black mt-[-5px] mb-[-7px] truncate search-item-input ${
                theme === 'vertical' ? 'text-[14px]' : ''
              }`}
            >
              {formatDate(value, locale)}
            </span>
          )}
        </div>
      </div>
      {value && <ButtonReset onClick={onReset} />}
    </div>
  );
}

function DateViewerCompact({ label, value, isFocused, onReset }) {
  const locale = useLocale();
  const t = useTranslations();
  const hasDates = value?.startDate || value?.endDate;
  return (
    <div
      className={`date-box search-input-box ${styles.box} ${
        isFocused ? 'bg-slate-100 !border-slate-300' : ''
      }`}
    >
      <div className={styles.input}>
        <span
          className={`icon-input ${styles.icon} ${
            isFocused ? '!text-teal-600' : ''
          }`}
        >
          <CalendarIcon />
        </span>
        <div className="flex flex-col gap-2 min-w-0 search-item-box">
          <label className="text-xs text-slate-600 truncate inline-block h-[18px] mt-[-4px] mb-[-5px]">
            <span>{label}</span>
          </label>
          {!hasDates && (
            <span className="inline-block font-medium text-slate-400 my-[-6px] truncate search-item-input max-sm:text-sm">
              {t('add-dates')}
            </span>
          )}
          {hasDates && (
            <span className="inline-block font-medium text-black my-[-6px] truncate search-item-input">
              {showDateRange(value, locale)}
            </span>
          )}
        </div>
      </div>
      {value && <ButtonReset onClick={onReset} />}
    </div>
  );
}

function ButtonReset({ onClick }) {
  return (
    <button
      className="bg-transparent self-center"
      type="button"
      onClick={onClick}
      tabIndex={1}
    >
      <CloseIcon />
    </button>
  );
}
